import React, { useMemo, useState } from 'react';
// Third-party
import { FormProvider, useForm } from 'react-hook-form';
import useTranslation from 'next-translate/useTranslation';

import clsx from 'clsx';

// Hooks
import useUser from '@/utils/hooks/swr/useUser';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import PropTypes from 'prop-types';

import { CAPTION, COMMENT, TAG } from '@/lib/manage/mentions';
// Lib
import { POST, STORY } from '@/lib/posts/postTypes';

import Comment from '@/components/base/Comment';
import Icon from '@/components/base/Icon';
import { MentionedIn } from '@/components/base/Mention/MentionedIn';
import LoadMoreButton from '@/components/composite/Button/LoadMoreButton';
import { CommentForm } from '@/components/composite/Comments/CommentForm';
// Components
import { MediaViewerPreview } from '@/components/composite/MediaViewer/components/MediaViewerPreview';

import MentionMediaViewerHeader from './MentionMediaViewerHeader';
import { MENTION_REPLY } from '@/routes/apiEndpoints';

const MentionMediaViewerContent = ({
    post,
    comments,
    captionReply,
    commentsIsLoadMore,
    loadMoreComments,
    commentsLoading,
    postType,
    isLoading,
    mutateMediaComments,
    addReply,
    hasNavigation = true
}) => {
    const formMethods = useForm({ mode: 'all', defaultValues: { message: '' } });
    const [activeFormReply, setActiveFormReply] = useState(null);
    const { t } = useTranslation('commons');
    const { user, isLoading: isUserLoading } = useUser();

    const { activeSocialProfile } = useActiveSocialProfile();

    const hasMentionedInTag = useMemo(
        () => post?.mentioned_in?.some((value) => value === TAG),
        [post]
    );

    const hasMentionedInComment = useMemo(
        () => post?.mentioned_in?.some((value) => value === COMMENT),
        [post]
    );

    const hasMentionedInCaption = useMemo(
        () => post?.mentioned_in?.some((value) => value === CAPTION),
        [post]
    );

    const normalizedComments = useMemo(() => {
        return {
            ...comments,
            data: comments?.data?.map((comment) => {
                comment.is_read = true;
                if (comment?.data?.reply) {
                    comment.data.reply.is_read = true;
                    comment.data.reply.data = comment?.data?.reply;
                }
                return comment;
            })
        };
    }, [comments]);

    return (
        <div className="flex min-h-full flex-col md:flex-row" id="media-viewer-content">
            <div
                className={clsx(
                    'w-full bg-white px-8 pb-8 pt-12 dark:bg-icoDarkMode-anthracite md:w-2/4 md:pb-20 md:pt-20',
                    {
                        'md:pl-20 md:pr-12 lg:pl-40 lg:pr-12': hasNavigation,
                        'md:px-12': !hasNavigation
                    }
                )}>
                <MentionMediaViewerHeader
                    post={post}
                    user={user}
                    isLoading={isLoading || isUserLoading}
                    className="mb-5 md:mb-9 md:mt-1"
                />
                <MediaViewerPreview
                    post={post}
                    postType={postType}
                    isLoading={isLoading}
                    openInNewTab={false}
                    remoteMedia
                />
            </div>
            <div
                className={clsx(
                    'relative w-full bg-icoGray-100 px-8 pb-12 pt-8 dark:bg-icoDarkMode-deepBlack md:w-2/4 md:px-5 md:pb-20 md:pt-20',
                    {
                        'lg:pl-12 lg:pr-40': hasNavigation,
                        'lg:px-12': !hasNavigation
                    }
                )}>
                {isLoading && !commentsLoading ? (
                    <RightPanePlaceholder />
                ) : (
                    <>
                        <div className="mb-10 flex items-center">
                            <Icon icon={'mentions'} className="mr-3 text-xl text-icoGray-500" />
                            <p>
                                {t('mention_media_viewer.your_mentions')}
                                <span className="ml-1 text-icoBlue">
                                    ({post?.mentioned_in?.length || 0})
                                </span>
                            </p>
                        </div>

                        {hasMentionedInTag && (
                            <div className="mb-10 flex flex-col">
                                <MentionedIn mentionCount={1} mentionType={TAG} large />

                                <p className="mt-4 font-GMedium text-sm">
                                    {t('mention_media_viewer.tagged_by', {
                                        mention: post?.username
                                    })}
                                </p>
                            </div>
                        )}

                        {hasMentionedInCaption && (
                            <>
                                {hasMentionedInTag ? <Separator /> : null}
                                <div
                                    className={clsx('mb-10 flex flex-col', {
                                        'pt-10': hasMentionedInTag
                                    })}>
                                    <MentionedIn mentionCount={1} mentionType={CAPTION} large />

                                    <div className="mt-4 rounded-md bg-white dark:bg-icoDarkMode-anthracite">
                                        <p className="p-8 font-GMedium text-sm leading-5">
                                            {post?.caption}
                                        </p>
                                    </div>
                                </div>
                            </>
                        )}

                        {hasMentionedInComment && (
                            <>
                                {hasMentionedInTag || hasMentionedInCaption ? <Separator /> : null}
                                <div
                                    className={clsx('mb-10 flex flex-col', {
                                        'pt-10': hasMentionedInTag || hasMentionedInCaption
                                    })}>
                                    <MentionedIn
                                        mentionCount={comments?.data?.length}
                                        mentionType={COMMENT}
                                        large
                                    />

                                    <div className="relative pt-5">
                                        {normalizedComments?.data?.map((comment) => (
                                            <Comment
                                                key={comment?.id}
                                                socialProfile={activeSocialProfile}
                                                comment={comment}
                                                user={user}
                                                activeFormReply={activeFormReply}
                                                handleActiveFormReply={(status) => {
                                                    setActiveFormReply(
                                                        status === 'open'
                                                            ? {
                                                                  parent: comment,
                                                                  currentCommentId: comment?.id
                                                              }
                                                            : null
                                                    );
                                                }}
                                                handleReplyToAdd={(reply) => {
                                                    addReply(reply);
                                                }}
                                                className="mb-3 rounded-md border-4 border-white p-4 dark:border-icoDarkMode-anthracite"
                                                displayOptions={{
                                                    displayDelete: false
                                                }}
                                                replyEndpoint={MENTION_REPLY}
                                                replyParams={{
                                                    mentionID: comment?.mention?.id
                                                }}
                                                replies={
                                                    <div
                                                        className={
                                                            'relative mt-2.5 flex flex-col space-y-1 border-l pl-2.5 dark:border-icoDarkMode-beton'
                                                        }>
                                                        {comment?.data?.reply ? (
                                                            <Comment
                                                                socialProfile={activeSocialProfile}
                                                                comment={comment?.data?.reply}
                                                                user={user}
                                                                displayOptions={{
                                                                    displayDelete: false,
                                                                    displayReply: false
                                                                }}
                                                            />
                                                        ) : null}
                                                    </div>
                                                }
                                            />
                                        ))}
                                        {commentsIsLoadMore && (
                                            <LoadMoreButton
                                                loadMoreItems={loadMoreComments}
                                                isLoadingMore={commentsLoading}
                                            />
                                        )}
                                    </div>
                                </div>
                            </>
                        )}

                        {captionReply ? (
                            <>
                                <Separator />
                                <div className={'relative flex flex-col space-y-1'}>
                                    <div className="my-10 flex items-center">
                                        <Icon
                                            icon={'comments'}
                                            className="mr-3 text-xl text-icoGray-500"
                                        />
                                        <p>{t('mention_media_viewer.your_comments')}</p>
                                    </div>
                                    <Comment
                                        socialProfile={activeSocialProfile}
                                        comment={captionReply}
                                        user={user}
                                        className="rounded-md border-4 border-white p-4 dark:border-icoDarkMode-anthracite"
                                        displayOptions={{
                                            displayDelete: false,
                                            displayReply: false
                                        }}
                                    />
                                </div>
                            </>
                        ) : null}
                        {hasMentionedInCaption ? (
                            <FormProvider {...formMethods}>
                                <CommentForm
                                    mediaId={post?.id}
                                    mutateComments={mutateMediaComments}
                                    submitOptions={{
                                        endpoint: MENTION_REPLY,
                                        params: { mentionID: post?.mention?.id }
                                    }}
                                />
                            </FormProvider>
                        ) : null}
                    </>
                )}
            </div>
        </div>
    );
};

MentionMediaViewerContent.propTypes = {
    mediaId: PropTypes.string,
    post: PropTypes.object,
    postType: PropTypes.oneOf([POST, STORY]),
    socialNetworkType: PropTypes.string,
    socialNetworkId: PropTypes.number,
    isLoading: PropTypes.bool,
    hasNavigation: PropTypes.bool,
    mutateMedia: PropTypes.func,
    mutateMedias: PropTypes.func
};

export default MentionMediaViewerContent;

const RightPanePlaceholder = () => {
    return (
        <>
            <div className="mb-10 block h-5 w-32 animate-pulse rounded-md bg-icoGray-300 dark:bg-icoDarkMode-anthracite" />

            <div className="mb-10 flex flex-col">
                <div className="mb-2 block h-5 w-24 animate-pulse rounded-sm bg-icoGray-300 dark:bg-icoDarkMode-anthracite" />
                <div className="block w-1/2 rounded bg-icoGray-300 py-1 dark:bg-icoDarkMode-anthracite" />
            </div>

            <Separator />

            <div className="mb-10 flex flex-col pt-10">
                <div className="mb-2 block h-5 w-24 animate-pulse rounded-sm bg-icoGray-300 dark:bg-icoDarkMode-anthracite" />
                <div className="block animate-pulse rounded-sm bg-icoGray-300 p-10 dark:bg-icoDarkMode-anthracite" />
            </div>

            <Separator />

            <div className="mb-10 flex flex-col pt-10">
                <div className="mb-2 block h-5 w-24 animate-pulse rounded-sm bg-icoGray-300 dark:bg-icoDarkMode-anthracite" />
                <div className="block animate-pulse rounded-sm bg-icoGray-300 p-10 dark:bg-icoDarkMode-anthracite" />
            </div>
        </>
    );
};

const Separator = () => (
    <span className="absolute left-0 w-full border-b border-icoGray-300 dark:border-icoDarkMode-beton" />
);
